import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutTemplate, PlusCircle, Eye, Star, MoreHorizontal, Search,
  X, AlertCircle, CheckCircle, ChevronDown, ChevronUp, ImageOff,
} from 'lucide-react';
import CreatorLayout from './CreatorLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

const STATUS_TABS = [
  { key: 'all',       label: 'All' },
  { key: 'draft',     label: 'Drafts' },
  { key: 'submitted', label: 'Under Review' },
  { key: 'published', label: 'Published' },
  { key: 'rejected',  label: 'Rejected' },
];

const STATUS_BADGE = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-amber-50 text-amber-700',
  published: 'bg-emerald-50 text-emerald-700',
  approved:  'bg-sky-50 text-sky-700',
  rejected:  'bg-red-50 text-red-600',
};

const STATUS_LABEL = {
  draft:     'Draft',
  submitted: 'Under Review',
  published: 'Published',
  approved:  'Approved',
  rejected:  'Rejected',
};

// ── Submission Modal ──────────────────────────────────────────────────────────

const SubmitModal = ({ template, categories, onClose, onSubmitted }) => {
  const [name, setName] = useState(template.name || '');
  const [description, setDescription] = useState(template.description || '');
  const [categoryId, setCategoryId] = useState(template.template_category_id || '');
  const [tagsInput, setTagsInput] = useState((template.tags || []).join(', '));
  const [licensing, setLicensing] = useState(template.licensing_type || 'free');
  const [price, setPrice] = useState(
    template.price_cents > 0 ? (template.price_cents / 100).toFixed(2) : ''
  );
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const errs = [];
    if (!name.trim()) errs.push('Title is required.');
    if (description.trim().length < 10) errs.push('Description must be at least 10 characters.');
    if (!categoryId) errs.push('Category is required.');
    if (licensing === 'one_time') {
      const p = parseFloat(price);
      if (isNaN(p) || p < 1) errs.push('Price must be at least $1.00 for paid templates.');
    }
    if (!template.thumbnail) errs.push('Thumbnail is missing — open the template in the editor and save it first.');
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (errs.length) { setErrors(errs); return; }

    setErrors([]);
    setSubmitting(true);
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
      const priceCents = licensing === 'one_time' ? Math.round(parseFloat(price) * 100) : 0;

      const metaRes = await fetch(`${API_URL}/api/creator/templates/${template.id}/metadata`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          template_category_id: categoryId,
          tags,
          licensing_type: licensing,
          price_cents: priceCents,
        }),
      });

      if (!metaRes.ok) {
        const d = await metaRes.json().catch(() => ({}));
        setErrors([d.detail || 'Failed to save metadata.']);
        setSubmitting(false);
        return;
      }

      const submitRes = await fetch(`${API_URL}/api/creator/templates/${template.id}/submit`, {
        method: 'POST',
        headers,
      });

      if (!submitRes.ok) {
        const d = await submitRes.json().catch(() => ({}));
        const detail = d.detail;
        if (detail?.errors) {
          setErrors(detail.errors);
        } else {
          setErrors([typeof detail === 'string' ? detail : 'Failed to submit template.']);
        }
        setSubmitting(false);
        return;
      }

      onSubmitted();
    } catch {
      setErrors(['An unexpected error occurred. Please try again.']);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-base font-bold text-gray-900">Submit for Review</h2>
            <p className="text-xs text-gray-400 mt-0.5">Complete all required fields before submitting.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 mb-1.5">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-700">Please fix the following:</p>
              </div>
              <ul className="list-disc list-inside space-y-0.5">
                {errors.map((e, i) => <li key={i} className="text-sm text-red-600">{e}</li>)}
              </ul>
            </div>
          )}

          {/* Thumbnail status */}
          <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50">
            <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {template.thumbnail
                ? <img src={template.thumbnail} alt="" className="w-full h-full object-cover" />
                : <ImageOff className="w-5 h-5 text-gray-400" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-700">Thumbnail</p>
              {template.thumbnail
                ? <p className="text-xs text-emerald-600 flex items-center gap-1 mt-0.5"><CheckCircle className="w-3 h-3" /> Thumbnail is set</p>
                : <p className="text-xs text-red-500 mt-0.5">No thumbnail — open in editor and save to generate one.</p>
              }
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Modern Business Card Pack"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-1">(min. 10 characters)</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe what's included, use cases, and what makes this template unique…"
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{description.length} chars</p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
            >
              <option value="">Select a category…</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Tags
              <span className="text-gray-400 font-normal ml-1">(comma-separated)</span>
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              placeholder="e.g. business, minimal, modern"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Pricing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pricing <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              {[
                { value: 'free',     label: 'Free',    desc: 'Available to everyone' },
                { value: 'one_time', label: 'Paid',    desc: 'One-time purchase' },
              ].map(opt => (
                <label
                  key={opt.value}
                  className={`flex-1 flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                    licensing === opt.value
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-gray-100 hover:border-gray-200 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="licensing"
                    value={opt.value}
                    checked={licensing === opt.value}
                    onChange={() => setLicensing(opt.value)}
                    className="mt-0.5 text-violet-600 focus:ring-violet-500"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-400">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Price amount (shown for paid) */}
          {licensing === 'one_time' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Price <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal ml-1">(min. $1.00)</span>
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">$</span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  placeholder="9.99"
                  className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>
          )}

          {/* License explanation */}
          <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
            <p className="text-xs font-semibold text-gray-600 mb-2">License terms for buyers</p>
            <div className="space-y-1">
              {[
                { ok: true,  text: 'Personal use' },
                { ok: true,  text: 'Commercial use' },
                { ok: true,  text: 'Unlimited exports' },
                { ok: false, text: 'Resale not allowed' },
                { ok: false, text: 'Redistribution not allowed' },
              ].map(({ ok, text }) => (
                <div key={text} className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${ok ? 'text-emerald-500' : 'text-red-400'}`}>
                    {ok ? '✓' : '✗'}
                  </span>
                  <span className="text-xs text-gray-500">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          >
            {submitting
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting…</>
              : 'Submit for Review'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Template Row ──────────────────────────────────────────────────────────────

const TemplateRow = ({ t, onEdit, onOpenSubmitModal }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showRejection, setShowRejection] = useState(false);
  const status = t.display_status || 'draft';

  return (
    <div className="border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors">
        {/* Thumbnail */}
        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {t.thumbnail
            ? <img src={t.thumbnail} alt="" className="w-full h-full object-cover" />
            : <LayoutTemplate className="w-5 h-5 text-gray-400" />
          }
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{t.name}</p>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[status] || 'bg-gray-100 text-gray-500'}`}>
              {STATUS_LABEL[status] || status}
            </span>
            {t.licensing_type === 'one_time' && t.price_cents > 0
              ? <span className="text-xs text-gray-400">${(t.price_cents / 100).toFixed(2)}</span>
              : t.licensing_type === 'free'
                ? <span className="text-xs text-gray-400">Free</span>
                : null
            }
            {status === 'rejected' && t.rejection_reason && (
              <button
                onClick={() => setShowRejection(o => !o)}
                className="flex items-center gap-0.5 text-xs text-red-500 hover:text-red-600 font-medium"
              >
                View reason
                {showRejection ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="hidden md:flex items-center gap-5 text-xs text-gray-400 flex-shrink-0">
          <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {t.views_count || 0}</span>
          <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {(t.average_rating || 0).toFixed(1)}</span>
          <span>{t.purchases_count || 0} sales</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {status === 'rejected' && (
            <button
              onClick={() => onOpenSubmitModal(t)}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
            >
              Fix & Resubmit
            </button>
          )}
          {status === 'draft' && (
            <button
              onClick={() => onOpenSubmitModal(t)}
              className="px-3 py-1.5 text-xs font-semibold text-violet-600 hover:text-violet-700 border border-violet-200 hover:border-violet-300 rounded-lg transition-colors"
            >
              Submit
            </button>
          )}

          <div className="relative">
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-8 z-40 bg-white rounded-xl shadow-lg border border-gray-100 py-1 w-44">
                  <button
                    onClick={() => { setMenuOpen(false); onEdit(t); }}
                    className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 text-gray-700"
                  >
                    Edit in editor
                  </button>
                  {(status === 'draft' || status === 'rejected') && (
                    <button
                      onClick={() => { setMenuOpen(false); onOpenSubmitModal(t); }}
                      className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 text-violet-600 font-medium"
                    >
                      {status === 'rejected' ? 'Fix & Resubmit' : 'Submit for review'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Rejection reason banner */}
      {status === 'rejected' && showRejection && t.rejection_reason && (
        <div className="mx-5 mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-red-700 mb-0.5">Rejection reason</p>
              <p className="text-sm text-red-600">{t.rejection_reason}</p>
              <button
                onClick={() => onOpenSubmitModal(t)}
                className="mt-2 text-xs font-semibold text-violet-600 hover:text-violet-700"
              >
                Fix issues and resubmit →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const CreatorTemplates = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalTemplate, setModalTemplate] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/template-categories`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    setLoading(true);
    const params = new URLSearchParams({ limit: '50' });
    if (tab !== 'all') params.set('status', tab);
    if (search.trim()) params.set('search', search.trim());
    fetch(`${API_URL}/api/creator/templates?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => { setTemplates(d.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [tab, search]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = (t) => navigate(`/editor/${t.id}?creatorMode=1`);

  const handleSubmitted = () => {
    setModalTemplate(null);
    load();
  };

  return (
    <CreatorLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">My Templates</h1>
        <button
          onClick={() => navigate('/creator/create')}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <PlusCircle className="w-4 h-4" /> New template
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5 w-fit overflow-x-auto">
        {STATUS_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              tab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search templates…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-16">
            <LayoutTemplate className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {tab === 'all' ? 'No templates yet' : `No ${STATUS_LABEL[tab]?.toLowerCase() || tab} templates`}
            </p>
            {tab === 'all' && (
              <button
                onClick={() => navigate('/creator/create')}
                className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 mx-auto transition-colors"
              >
                <PlusCircle className="w-4 h-4" /> Create template
              </button>
            )}
          </div>
        ) : (
          <div>
            {templates.map(t => (
              <TemplateRow
                key={t.id}
                t={t}
                onEdit={handleEdit}
                onOpenSubmitModal={setModalTemplate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Submit Modal */}
      {modalTemplate && (
        <SubmitModal
          template={modalTemplate}
          categories={categories}
          onClose={() => setModalTemplate(null)}
          onSubmitted={handleSubmitted}
        />
      )}
    </CreatorLayout>
  );
};

export default CreatorTemplates;
